/**
 * Phase 113.D — "Schedule remaining lessons" quick-action dialog.
 *
 * Surfaces a unit's unscheduled lessons placed onto the next available
 * subject slots, then lets the teacher edit each placement (or remove
 * it) before committing. Strictly review-then-confirm — nothing is
 * written to the Planner until the teacher clicks "Confirm schedule".
 *
 * Triggered from two entry points:
 *  - The Long Range Plan unit card ("Schedule lessons →")
 *  - The Planner header picker ("Schedule from Long Range Plan")
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Loader2,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  commitScheduleForUnit,
  getPlannerSchedule,
  proposeScheduleForUnit,
} from "../lib/api";

const FIELD_CLASS =
  "bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus:ring-2 focus:ring-[#96ded1] focus-visible:ring-[#96ded1] rounded-md h-9 text-sm";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

const ScheduleRemainingLessonsDialog = ({
  open,
  onOpenChange,
  unitActivityId,
  unitName,
  onCommitted,
}) => {
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [subject, setSubject] = useState("");
  const [resolvedUnitName, setResolvedUnitName] = useState(unitName || "");
  // rows = list of { lesson_id, lesson_name, lesson_index, date,
  // period_label, day_label, ok, removed, periodOptions: [string] }
  const [rows, setRows] = useState([]);
  const [periodLabels, setPeriodLabels] = useState([]);

  // Reset + fetch on open.
  useEffect(() => {
    if (!open || !unitActivityId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setWarning("");
    setRows([]);
    Promise.all([
      proposeScheduleForUnit(unitActivityId),
      getPlannerSchedule().catch(() => null),
    ])
      .then(([res, sched]) => {
        if (cancelled) return;
        if (res?.error) {
          setError(res.error);
          setSubject(res.subject || "");
          setResolvedUnitName(res.unit_name || unitName || "");
          return;
        }
        setSubject(res?.subject || "");
        setResolvedUnitName(res?.unit_name || unitName || "");
        setWarning(res?.warning || "");
        const placements = Array.isArray(res?.placements) ? res.placements : [];
        setRows(
          placements.map((p) => ({
            lesson_id: p.lesson_id,
            lesson_name: p.lesson_name,
            lesson_index: p.lesson_index,
            date: p.date || "",
            period_label: p.period_label || "",
            day_label: p.day_label || "",
            ok: Boolean(p.ok),
            removed: false,
          }))
        );
        // Build the period dropdown options from the schedule (filter
        // out break-kind periods — same logic the backend applies).
        const labels = (sched?.periods || [])
          .filter((p) => (p?.kind || "lesson") !== "break")
          .map((p) => p.label);
        setPeriodLabels(labels);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load proposed schedule. Try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, unitActivityId, unitName]);

  const eligibleRows = useMemo(
    () => rows.filter((r) => !r.removed && r.ok && r.date && r.period_label),
    [rows]
  );

  const handleCommit = async () => {
    if (!unitActivityId || eligibleRows.length === 0) return;
    setCommitting(true);
    try {
      const payload = eligibleRows.map((r) => ({
        lesson_id: r.lesson_id,
        date: r.date,
        period_label: r.period_label,
      }));
      const res = await commitScheduleForUnit(unitActivityId, payload);
      const created = res?.created?.length || 0;
      const skipped = res?.skipped || 0;
      if (created > 0) {
        toast.success(
          `${created} lesson${created === 1 ? "" : "s"} scheduled${
            skipped ? ` · ${skipped} skipped` : ""
          }.`
        );
      } else {
        toast.info("Nothing new was scheduled.");
      }
      if (typeof onCommitted === "function") onCommitted(res);
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not schedule lessons. Try again."
      );
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-white"
        data-testid="schedule-remaining-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#1c2d3d] font-extrabold flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-[#1c9e7e]" />
            Proposed schedule — {resolvedUnitName || "Unit"}
          </DialogTitle>
          <DialogDescription className="text-[#4a6984]">
            Review the placements below. Adjust dates or periods if you need to,
            remove any you don&apos;t want, then click <strong>Confirm schedule</strong>{" "}
            to write them to your Planner. Nothing is scheduled until you confirm.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-[#4a6984]">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading proposed schedule…
          </div>
        ) : error ? (
          <div
            data-testid="schedule-remaining-error"
            className="rounded-lg border border-[#f59e0b]/40 bg-[#fff7ed] p-4 text-sm text-[#7c2d12] flex items-start gap-2"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>{error}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-[#4a6984]">
            <p className="text-sm">No unscheduled lessons in this unit.</p>
          </div>
        ) : (
          <>
            {warning ? (
              <div
                data-testid="schedule-remaining-warning"
                className="rounded-lg border border-[#f59e0b]/40 bg-[#fff7ed] p-3 text-xs text-[#7c2d12] flex items-start gap-2"
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <p>{warning}</p>
              </div>
            ) : null}
            <ul
              className="space-y-2 max-h-[50vh] overflow-y-auto pr-1"
              data-testid="schedule-remaining-list"
            >
              {rows.map((r, idx) => {
                if (r.removed) {
                  return (
                    <li
                      key={r.lesson_id}
                      data-testid={`schedule-remaining-row-${idx}-removed`}
                      className="flex items-center justify-between gap-3 rounded-md border border-dashed border-[#4a6984]/25 bg-[#f4f5f7]/40 px-3 py-2 text-[12px] text-[#4a6984]/65"
                    >
                      <span className="line-through">
                        Lesson {r.lesson_index} — {r.lesson_name}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setRows((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, removed: false } : row
                            )
                          )
                        }
                        data-testid={`schedule-remaining-row-${idx}-undo`}
                        className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#1c9e7e] hover:underline"
                      >
                        Undo
                      </button>
                    </li>
                  );
                }
                return (
                  <li
                    key={r.lesson_id}
                    data-testid={`schedule-remaining-row-${idx}`}
                    className={`rounded-md border px-3 py-2.5 ${
                      r.ok
                        ? "border-[#96ded1] bg-[#96ded1]/10"
                        : "border-[#f59e0b]/40 bg-[#fff7ed]"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-extrabold text-[13px] text-[#1c2d3d] flex items-center gap-1.5">
                          {r.ok ? (
                            <CheckCircle2
                              className="h-3.5 w-3.5 text-[#1c9e7e] shrink-0"
                              aria-label="This slot normally hosts this subject"
                            />
                          ) : (
                            <AlertTriangle className="h-3.5 w-3.5 text-[#b45309] shrink-0" />
                          )}
                          Lesson {r.lesson_index}
                          <span className="text-[#4a6984] font-medium truncate">
                            · {r.lesson_name}
                          </span>
                        </p>
                        {!r.ok ? (
                          <p className="text-[11px] text-[#7c2d12] mt-1">
                            No upcoming {subject} slot — schedule manually or
                            adjust your Planner.
                          </p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setRows((prev) =>
                            prev.map((row, i) =>
                              i === idx ? { ...row, removed: true } : row
                            )
                          )
                        }
                        data-testid={`schedule-remaining-row-${idx}-remove`}
                        aria-label="Remove from this batch"
                        className="text-[#4a6984]/60 hover:text-red-600 shrink-0"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {r.ok ? (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <Input
                          type="date"
                          value={r.date}
                          onChange={(e) =>
                            setRows((prev) =>
                              prev.map((row, i) =>
                                i === idx
                                  ? { ...row, date: e.target.value }
                                  : row
                              )
                            )
                          }
                          data-testid={`schedule-remaining-row-${idx}-date`}
                          className={FIELD_CLASS}
                        />
                        <Select
                          value={r.period_label}
                          onValueChange={(v) =>
                            setRows((prev) =>
                              prev.map((row, i) =>
                                i === idx
                                  ? { ...row, period_label: v }
                                  : row
                              )
                            )
                          }
                        >
                          <SelectTrigger
                            data-testid={`schedule-remaining-row-${idx}-period`}
                            className={FIELD_CLASS}
                          >
                            <SelectValue placeholder="Period" />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {periodLabels.length === 0 ? (
                              <SelectItem value={r.period_label} disabled>
                                {r.period_label || "—"}
                              </SelectItem>
                            ) : (
                              periodLabels.map((p) => (
                                <SelectItem key={p} value={p}>
                                  {p}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : null}
                    <p className="text-[10px] text-[#4a6984]/70 mt-1.5 truncate">
                      Suggested: {fmtDate(r.date)}{" "}
                      {r.day_label ? `· ${r.day_label}` : ""}
                    </p>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={committing}
            data-testid="schedule-remaining-cancel"
          >
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button
            type="button"
            onClick={handleCommit}
            disabled={
              loading ||
              committing ||
              Boolean(error) ||
              eligibleRows.length === 0
            }
            className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold"
            data-testid="schedule-remaining-confirm"
          >
            {committing ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Scheduling…
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Confirm schedule
                {eligibleRows.length > 0 ? ` (${eligibleRows.length})` : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleRemainingLessonsDialog;
